/**
 * Утилиты для работы с элементами диаграммы
 */

import { getEdgePoint } from '../types/elements';

export const getCenter = (el) => {
  if (!el) return { x: 0, y: 0 };
  if (el.type === 'rectangle' || el.type === 'cylinder') return { x: el.x + el.width / 2, y: el.y + el.height / 2 };
  if (el.type === 'circle') return { x: el.x + el.radius, y: el.y + el.radius };
  return { x: el.x, y: el.y };
};

export const isConnectable = (e) => e && (e.type === 'rectangle' || e.type === 'circle' || e.type === 'cylinder');

export const textWidth = (t, fs = 16) => Math.max(1, Math.ceil((t || '').length * Math.max(8, Math.min(fs, 96)) * 0.7));

export const bboxOf = (els) => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  els.forEach(el => {
    if (!el) return;

    if (el.width !== undefined && el.height !== undefined && el.x !== undefined && el.y !== undefined) {
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + el.width);
      maxY = Math.max(maxY, el.y + el.height);
    }
    else if (el.type === 'circle' || el.radius !== undefined) {
      const r = el.radius || 0;
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + 2 * r);
      maxY = Math.max(maxY, el.y + 2 * r);
    }
    else if (el.type === 'arrow') {
      const pts = el.pathPoints?.length ? el.pathPoints : [{ x: el.x, y: el.y }, { x: el.x2, y: el.y2 }];
      pts.forEach(p => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      });
    } else if (el.type === 'text') {
      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x);
      maxY = Math.max(maxY, el.y);
    }
  });

  if (!isFinite(minX)) return null;
  return { minX, minY, maxX, maxY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
};

export const shift = (els, dx, dy) => els.map(el => {
  if (el.type === 'arrow') {
    const copy = { ...el, x: el.x + dx, y: el.y + dy, x2: el.x2 + dx, y2: el.y2 + dy };
    if (el.pathPoints) {
      copy.pathPoints = el.pathPoints.map(p => ({ x: p.x + dx, y: p.y + dy }));
    }
    return copy;
  }
  // Для всех остальных элементов с координатами x, y
  if (el.x !== undefined && el.y !== undefined) {
    return { ...el, x: el.x + dx, y: el.y + dy };
  }
  return el;
});

export const snapArrows = (els, existingElements, findNearestConnectionPoint) => {
  const pool = [...existingElements, ...els];

  const resolveSide = (connection, ownX, ownY, otherX, otherY) => {
    const fallbackSide = connection.point.side;
    const element = connection.element;
    if (typeof element?.width !== 'number' || typeof element?.height !== 'number') {
      return fallbackSide;
    }

    const cx = element.x + element.width / 2;
    const cy = element.y + element.height / 2;
    const dx = ownX - cx;
    const dy = ownY - cy;

    // Auto-generated arrows often start from the exact class center.
    // In that ambiguous case, choose the border by the opposite endpoint direction.
    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
      return fallbackSide;
    }

    const otherDx = otherX - cx;
    const otherDy = otherY - cy;
    if (Math.abs(otherDx) > Math.abs(otherDy)) {
      return otherDx >= 0 ? 'right' : 'left';
    }
    return otherDy >= 0 ? 'bottom' : 'top';
  };

  return els.map(el => {
    if (el.type !== 'arrow' || el.isMessage || el.isFrameSeparator) return el;
    if (el.lifeline?.start && el.lifeline?.end) return el;

    const sc = findNearestConnectionPoint(el.x, el.y, pool, el, Infinity);
    const ec = findNearestConnectionPoint(el.x2, el.y2, pool, el, Infinity);
    const up = { ...el };

    if (sc) {
      const side = resolveSide(sc, el.x, el.y, el.x2, el.y2);
      const edgePoint = getEdgePoint(sc.element, side);
      up.x = edgePoint.x;
      up.y = edgePoint.y;
      up.startConnection = { elementId: sc.element.id, side };
    }
    if (ec) {
      const side = resolveSide(ec, el.x2, el.y2, el.x, el.y);
      const edgePoint = getEdgePoint(ec.element, side);
      up.x2 = edgePoint.x;
      up.y2 = edgePoint.y;
      up.endConnection = { elementId: ec.element.id, side };
    }

    return up;
  });
};

/** Returns the canvas point where an attached text element should be centered. */
export const getTextAnchor = (parent) => {
  const cx = parent.type === 'circle'
    ? parent.x + parent.radius
    : parent.x + (parent.width || 0) / 2;
  const cy = parent.type === 'circle'
    ? parent.y + parent.radius
    : parent.y + (parent.height || 0) / 2;

  const kind = parent.c4?.kind;
  if ((kind === 'person' || kind === 'external-person') && parent.width && parent.height) {
    const headR = Math.round(parent.width * 0.22);
    const bodyY = parent.y + headR + 2 + headR + 10;
    return { cx, cy: (bodyY + parent.y + parent.height) / 2 };
  }

  return { cx, cy };
};

export const centerTexts = (els, existingElements) => {
  const map = Object.fromEntries(els.map(e => [e.id, e]));
  return els.map(el => {
    if (el.type !== 'text' || !el.attachedToElement) return el;

    const parent = map[el.attachedToElement] || existingElements.find(e => e.id === el.attachedToElement);
    if (!parent || !isConnectable(parent)) return el;

    const { cx, cy } = getTextAnchor(parent);
    return { ...el, x: cx, y: cy };
  });
};

export const autosizeShapes = (els) => {
  const byParent = new Map();
  els.forEach(el => {
    if (el.type === 'text' && el.attachedToElement) {
      const arr = byParent.get(el.attachedToElement) || [];
      arr.push(el);
      byParent.set(el.attachedToElement, arr);
    }
  });

  if (!byParent.size) return els;

  return els.map(el => {
    if (!['rectangle', 'circle', 'cylinder'].includes(el.type) || !byParent.has(el.id)) return el;
    if (el.c4?.kind) return el; // C4 elements keep their preset dimensions

    const texts = byParent.get(el.id);
    const fs = Math.max(8, Math.min(96, Math.max(...texts.map(t => t.fontSize || 16))));
    const maxW = Math.max(...texts.map(t => textWidth(t.content || '', t.fontSize || fs)));

    if (el.type === 'rectangle' || el.type === 'cylinder') {
      const cx = el.x + el.width / 2, cy = el.y + el.height / 2;
      const width = Math.max(el.width, Math.max(120, maxW + 40));
      const height = Math.max(el.height, Math.max(40, Math.ceil(fs + 20)));
      return { ...el, x: cx - width / 2, y: cy - height / 2, width, height };
    } else {
      const cx = el.x + el.radius, cy = el.y + el.radius;
      const r = Math.max(el.radius, Math.ceil(Math.max(maxW / 2 + 20, fs / 2 + 10)));
      return { ...el, x: cx - r, y: cy - r, radius: r };
    }
  });
};
